import type { ReactNode } from 'react'
import { ApiError } from '../lib/api'

/**
 * Loading, empty and error states defined once (SW-601).
 *
 * Every screen uses these rather than rolling its own. Three screens with three different
 * empty states is how an app stops feeling native, and reviewers explicitly assess that.
 */

export function LoadingState({ label = 'Loading' }: { label?: string }) {
  return (
    <s-section>
      <s-stack direction="inline" gap="base" alignItems="center">
        <s-spinner size="base" accessibilityLabel={label} />
        <s-text color="subdued">{label}…</s-text>
      </s-stack>
    </s-section>
  )
}

/**
 * The empty state **teaches rather than showing zeros** (SW-602). A merchant on day one needs
 * to know what to do next; a blank chart tells them the app is broken.
 *
 * The mark is the one piece of StockWait branding in the embedded admin, and it is here on
 * purpose. Everything else stays Polaris: an embedded app is assessed on whether it feels
 * native to the Shopify admin, so restyling controls in our own palette would actively work
 * against Built for Shopify (SW-1002). An empty state is the conventional place a Shopify app
 * is allowed an illustration, so that is where the brand goes.
 */
export function EmptyState({
  heading,
  children,
  action,
  branded = true,
}: {
  heading: string
  children: ReactNode
  action?: { label: string; onClick: () => void }
  /** Set false where an illustration would be noise rather than welcome. */
  branded?: boolean
}) {
  return (
    <s-section>
      <s-stack direction="block" gap="base">
        {branded ? (
          <s-box maxInlineSize="88px">
            <s-image
              src="/brand/mark-512.png"
              alt=""
              inlineSize="fill"
              aspectRatio="1/1"
              objectFit="contain"
              loading="lazy"
            />
          </s-box>
        ) : null}
        <s-heading>{heading}</s-heading>
        <s-paragraph color="subdued">{children}</s-paragraph>
        {action ? (
          <s-stack direction="inline">
            <s-button variant="primary" onClick={action.onClick}>
              {action.label}
            </s-button>
          </s-stack>
        ) : null}
      </s-stack>
    </s-section>
  )
}

/**
 * Error state.
 *
 * A plan gate is not a fault, so it renders as an upsell that says what the feature does and
 * what it costs, hiding a paid feature sells nothing (SW-702).
 */
export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  if (error instanceof ApiError && error.isUpgradeRequired) {
    const description = typeof error.body.description === 'string' ? error.body.description : null
    const plan = typeof error.body.availableOn === 'string' ? error.body.availableOn : null

    return (
      <s-section>
        <s-banner tone="info" heading="Available on a higher plan">
          <s-stack direction="block" gap="base">
            <s-paragraph>
              {description ?? 'This feature is not included in your current plan.'}
            </s-paragraph>
            {plan ? <s-paragraph color="subdued">Included on the {plan} plan.</s-paragraph> : null}
            <s-stack direction="inline">
              <s-button variant="primary" href="/plans">
                See plans
              </s-button>
            </s-stack>
          </s-stack>
        </s-banner>
      </s-section>
    )
  }

  const message = error instanceof Error ? error.message : 'Something went wrong.'

  return (
    <s-section>
      <s-banner tone="critical" heading="Something went wrong">
        <s-stack direction="block" gap="base">
          <s-paragraph>{message}</s-paragraph>
          {onRetry ? (
            <s-stack direction="inline">
              <s-button onClick={onRetry}>Try again</s-button>
            </s-stack>
          ) : null}
        </s-stack>
      </s-banner>
    </s-section>
  )
}

/** A headline metric. Grouped so the dashboard's four numbers read as one row, not four cards. */
export function MetricTile({
  label,
  value,
  detail,
  tone,
}: {
  label: string
  value: string
  detail?: string
  tone?: 'success' | 'info' | 'caution'
}) {
  return (
    <s-box padding="base" borderRadius="base" background="subdued">
      <s-stack direction="block" gap="small-400">
        <s-text color="subdued" type="strong">
          {label}
        </s-text>
        <s-heading accessibilityRole="none">{value}</s-heading>
        {detail ? (
          tone ? (
            <s-badge tone={tone}>{detail}</s-badge>
          ) : (
            <s-text color="subdued">{detail}</s-text>
          )
        ) : null}
      </s-stack>
    </s-box>
  )
}
